/*
 * Header search modal (also the base of the password page modal).
 *
 * The header search trigger is a <button data-modal-toggle aria-haspopup="dialog">,
 * not a <summary>, because <summary> must not be used to open a dialog. Its
 * open/close state is tracked with an `open` attribute on the <details-modal>
 * host element.
 *
 * A modal that still wraps its content in <details>/<summary> (the password
 * page) keeps working: the <summary> is the toggle and the `open` attribute
 * lives on the <details>, as before.
 */
class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.stateElement = this.detailsContainer || this;
    this.toggle = this.querySelector('[data-modal-toggle]') || this.querySelector('summary');

    this.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.toggle.addEventListener('click', this.onToggleClick.bind(this));

    if (this.toggle.tagName === 'SUMMARY') {
      this.toggle.setAttribute('role', 'button');
      this.toggle.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.onToggleClick(event);
        }
      });
    }

    const closeButton = this.querySelector('.modal__close-button') || this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));
  }

  isOpen() {
    return this.stateElement.hasAttribute('open');
  }

  onToggleClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open() {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    this.stateElement.setAttribute('open', '');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(this.querySelector('[tabindex="-1"]'), this.querySelector('input:not([type="hidden"])'));
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.toggle : null);
    this.stateElement.removeAttribute('open');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);
